import { Disclosure } from '@headlessui/react';
import { Role, rolesManipulator } from 'auth';
import classNames from 'classnames';
import dayjs from 'dayjs';
import isEmpty from 'lodash/isEmpty';
import { useCallback, useContext, useMemo, useState } from 'react';
import { withPrivateAccess } from '@/modules/auth/hocs/withPrivateAccess.tsx';
import CertificatesFilters from '@/modules/common/components/CertificatesFilters.tsx';
import { useInvestorGetCertificatesQuery } from '@/modules/investor-dashboard/queries/certificates.ts';
import { IssuerAddAnnouncementModal } from '@/modules/issuer-dashboard/components/modals/IssuerAddAnnouncementModal.tsx';
import { useIssuerGetCertificatesQuery } from '@/modules/issuer-dashboard/queries/certificates.ts';
import {
  Badge,
  EmptyListState,
  ModalProviderContext,
  PageLayout,
  Table,
  TableBodyColumn,
  TableHeadColumn,
} from 'ui';
import { DownloadIcon, FiltersIcon, SendGreyIcon } from 'ui/icons';
import { goodFormatNumber, goodFormatUSDCurrency } from 'ui/utils';
import {
  GetListByIssuerV1SrCertificatesIssuerGetParams,
  ShareCertificateResponse,
  ShareCertificateStatus,
} from 'api';

export type Filters = Pick<
  GetListByIssuerV1SrCertificatesIssuerGetParams,
  'from_date' | 'to_date' | 'status'
>;

interface CertificatesProps {
  firstColumnType: 'investor' | 'register';
}

const Certificates = ({ firstColumnType }: CertificatesProps) => {
  const { openModal } = useContext(ModalProviderContext);
  const [filters, setFilters] = useState<Filters>({});
  const userRole = rolesManipulator.current_user_role;

  const getFunction =
    userRole === Role.SR_ISSUER || userRole === Role.SR_ISSUER_ADMIN
      ? useIssuerGetCertificatesQuery
      : useInvestorGetCertificatesQuery;

  const { data, fetchNextPage, hasNextPage, isFetching, isLoading } = getFunction({
    page: 1,
    page_size: 30,
    status: filters.status,
    from_date: filters.from_date,
    to_date: filters.to_date,
  });

  const certificates = (data?.pages || []).flatMap(({ data }) => data);

  const headItems = useMemo<TableHeadColumn[]>(
    () => [
      {
        title: `${firstColumnType === 'investor' ? 'Investor' : ''}${
          firstColumnType === 'register' ? 'Register' : ''
        }`,
      },
      { title: 'Class' },
      { title: 'Issue price' },
      { title: 'Shares/units' },
      { title: 'Total value' },
      { title: 'Date' },
      { title: 'Status' },
      { title: '', className: 'min-w-12' },
    ],
    [firstColumnType],
  );

  const handleOpenCommunicationModal = useCallback(
    (item: ShareCertificateResponse) => () => {
      const { entity, register_data, register_class } = item;
      openModal(IssuerAddAnnouncementModal, {
        certificates: [
          {
            label: (
              <div className="text-sm leading-[22px] font-[Inter] flex items-center gap-2">
                <div className="text-gray-900 whitespace-nowrap 2xl:max-w-[350px] sm:max-w-[200px] max-w-[130px] overflow-hidden text-ellipsis">
                  {entity?.entity_name || register_data?.name || '---'}
                </div>
              </div>
            ),
            value: item,
          },
        ],
        registerClass: register_class
          ? {
              value: register_class.id,
              label: register_class.class_name,
            }
          : undefined,
      });
    },
    [openModal],
  );

  const items = useMemo<TableBodyColumn[]>(
    () =>
      certificates.map(certificate => {
        const {
          status,
          entity,
          created_at,
          register_data,
          register_class,
          price_per_share,
          quantity_of_shares,
          file_storage_item_url,
        } = certificate;
        return {
          cells: [
            {
              cell: (
                <>
                  {firstColumnType === 'investor' && <span>{entity?.entity_name ?? '---'}</span>}
                  {firstColumnType === 'register' && <span>{register_data.name}</span>}
                </>
              ),
            },
            { cell: register_class?.class_name || '-' },
            { cell: goodFormatUSDCurrency(price_per_share) },
            { cell: goodFormatNumber(quantity_of_shares) },
            { cell: goodFormatUSDCurrency(price_per_share * quantity_of_shares) },
            { cell: dayjs(created_at).format('DD-MM-YYYY') },
            {
              cell: (
                <Badge
                  className={classNames('text-white capitalize w-fit', {
                    'bg-green-600': status === ShareCertificateStatus.Current,
                    'bg-gray-400': status === ShareCertificateStatus.Outdated,
                  })}
                >
                  {status}
                </Badge>
              ),
            },
            {
              cell: (
                <div className="flex gap-4 select-none items-center sm:justify-end justify-center">
                  {userRole === Role.SR_ISSUER ||
                    (userRole === Role.SR_ISSUER_ADMIN && (
                      <button onClick={handleOpenCommunicationModal(certificate)}>
                        <img src={SendGreyIcon} alt="send" />
                      </button>
                    ))}
                  {file_storage_item_url && (
                    <span
                      className="cursor-pointer hover:opacity-80"
                      onClick={e => e.stopPropagation()}
                    >
                      <a href={file_storage_item_url} target="_blank" rel="noopener noreferrer">
                        <img
                          alt="cerificate"
                          src={DownloadIcon}
                          className="w-6 h-6 min-w-[24px] min-h-[24px]"
                        />
                      </a>
                    </span>
                  )}
                </div>
              ),
            },
          ],
        };
      }),
    [certificates, firstColumnType, handleOpenCommunicationModal, userRole],
  );

  const headerAction = (
    <Disclosure.Button>
      <div className="cursor-pointer flex items-center border px-4 py-[0.5rem] rounded border-[rgba(23, 23, 23, 0.12)] gap-2">
        <img alt="filters" src={FiltersIcon} />
        <span className="text-sm">Filters</span>
      </div>
    </Disclosure.Button>
  );

  return (
    <PageLayout
      title="Certificates"
      HeaderWrapper={Disclosure}
      headerAction={headerAction}
      headerBottomContent={
        <CertificatesFilters filters={filters} setFilters={setFilters} isFetching={isFetching} />
      }
    >
      {(!!items.length || isLoading) && (
        <Table
          classNameHead="border-t-0"
          withBorderX={false}
          isLoading={isLoading}
          fetchNextPageInfinite={fetchNextPage}
          hasNextPageInfinite={hasNextPage}
          headItems={headItems}
          items={items}
          isSearchUsed={!isEmpty(filters)}
          blockIdInfinite="scrollable"
        />
      )}

      <EmptyListState
        title="No certificates yet"
        description="It seems there are no certificates to display at the moment. Certificates are automatically generated after balance updates or transactions are completed."
        isLoading={isFetching}
        isSearchUsed={!isEmpty(filters)}
        itemsCount={items.length}
      />
    </PageLayout>
  );
};

export default withPrivateAccess(Certificates, [
  Role.SR_INVESTOR,
  Role.SR_ISSUER,
  Role.SR_ISSUER_ADMIN,
]);
